import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {Floorplan} from '@domain/machine/floorplan';
import {MachineFile} from '@domain/machine/machine-file';
import {PropertyValue} from '@domain/property-value';
import {OverviewMachine} from '@presentation/pages/machine-overview/overview-machine';
import {OverviewMachineGroup} from '@presentation/pages/machine-overview/overview-machine-group';
import {MachineOverviewMessageFilter} from '@presentation/pages/machine-overview/sidebar/machine-overview-message-filter';
import {Message} from '@vdw/angular-component-library';
import {map} from 'lodash-es';
import {Observable} from 'rxjs';
import {map as rxjsMap} from 'rxjs/operators';
import {MachineOverview} from './machine-overview';

@Injectable()
export class HttpMachineOverviewService implements MachineOverview {
  private httpClient: HttpClient;
  private backendConfiguration: BackendConfiguration;

  public constructor(httpClient: HttpClient, backendConfiguration: BackendConfiguration) {
    this.httpClient = httpClient;
    this.backendConfiguration = backendConfiguration;
  }

  public getListOfCustomSettings(): Observable<PropertyValue[]> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/custom-settings`).pipe(
      rxjsMap((result: PropertyValue[]) => {
        return result;
      })
    );
  }

  public saveCustomSettings(chosenSettings: PropertyValue[]): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/custom-settings`, chosenSettings);
  }

  public updateMachinePosition(machine: OverviewMachine): Observable<void> {
    machine.coordinatesInPx.x = Math.round(machine.coordinatesInPx.x);
    machine.coordinatesInPx.y = Math.round(machine.coordinatesInPx.y);
    const newPositionObject = {
      machineId: machine.id,
      coordinatesInPx: machine.coordinatesInPx
    };
    return this.httpClient.put<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/floorplan/machineposition`, newPositionObject);
  }

  public addMachineToFloorplan(machine: OverviewMachine): Observable<void> {
    machine.coordinatesInPx.x = Math.round(machine.coordinatesInPx.x);
    machine.coordinatesInPx.y = Math.round(machine.coordinatesInPx.y);
    const newPositionObject = {
      machineId: machine.id,
      coordinatesInPx: machine.coordinatesInPx
    };
    return this.httpClient.post<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/floorplan/machine`, newPositionObject);
  }

  public getFloorplan(): Observable<Floorplan> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/floorplan`).pipe(
      rxjsMap((result: {machines}) => {
        const machines = map(result.machines, (machine: any) => {
          return OverviewMachine.fromJSON(machine);
        });

        return new Floorplan({heightInPx: null, widthInPx: null}, machines);
      })
    );
  }

  public hasAlreadyAdjustedCustomSettings(): Observable<boolean> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/custom-settings/already-adjusted`).pipe(
      rxjsMap((result: {alreadyAdjustedCustomSettings}) => {
        return result.alreadyAdjustedCustomSettings;
      })
    );
  }

  public getMessages(start: number, count: number, filter: MachineOverviewMessageFilter): Observable<Message[]> {
    return this.httpClient.post(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/messages?count=${count}&start=${start}`, filter).pipe(
      rxjsMap((messages: any[]) => {
        return map(messages, (message: any) => Message.fromJSON(message));
      })
    );
  }

  public saveMachineFiles(machineId: number, files: MachineFile[]): Observable<void> {
    return this.httpClient.post<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinefiles/machine/${machineId}`, files);
  }

  public deleteMachineFiles(machineId: number, files: MachineFile[]): Observable<void> {
    const options = {
      headers: {},
      body: map(files, 'name')
    };

    return this.httpClient.delete<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinefiles/machine/${machineId}`, options);
  }

  public getRemoteAccessUrl(machineId: number): Observable<string> {
    return this.httpClient.get<string>(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/${machineId}/remote-access-url`);
  }

  public getRootGroup(): Observable<OverviewMachineGroup> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machineoverview/machine-groups`)
      .pipe(rxjsMap((machineGroupJSON: any) => OverviewMachineGroup.fromJSON(machineGroupJSON)));
  }
}
